import { useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useFeatureIsOn, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { AppConfig, usePermissions } from '@ltvco/refresh-lib/ctx';
import {
  invalidateSession,
  isUserLoggedIn,
  updateSetting,
  useSession,
} from '@ltvco/refresh-lib/v1';
import { useReactivation } from '@ltvco/refresh-lib/payments';
import { Box, CircularProgress } from '@ltvco/refresh-lib/theme';
import { CookiesStorage } from '@ltvco/refresh-lib';
import { getFreemiumSubscribePageData } from '../utils/freemiumSubscribePageStorage';
import { isEligibleForMemberPerks } from '../utils/memberPerks';

interface AuthenticatedRouteProps {
  children: JSX.Element;
  alternateReactivationPath?: string;
}

export function AuthenticatedRoute({
  children,
  alternateReactivationPath,
}: AuthenticatedRouteProps) {
  const cookieStorage = new CookiesStorage();
  const redirectOnLoginCookie = cookieStorage.getValue(
    'free_activation_redirect',
  );
  const [isLoading, setIsLoading] = useState(true);
  const {
    session: { isAuthenticated, account },
  } = useSession(); // should be replaced by the user_info object
  const { isFreePlan } = usePermissions();
  const isMemberPerksEnabled = useFeatureIsOn('BUMP-5186');
  const queryClient = useQueryClient();
  const isNonCompliant = account?.account.user_info.noncompliant;
  const { routingUtils, logError } = useContext(AppConfig);
  const { location: locationAppConfig } = routingUtils;

  const location = useLocation();
  const navigate = useNavigate();

  const complianceRoutes = [
    '/compliance-form',
    '/noncompliance',
    '/compliance-prohibited',
    '/compliance-permitted',
    '/compliance-recomply',
  ];

  const activateFreeAccessRoute = '/activate-free-access';

  const useReactivationProps: {
    currentPathName?: string;
    reactivationPath?: string;
  } = {
    currentPathName: location.pathname,
  };

  if (alternateReactivationPath || redirectOnLoginCookie === 'true') {
    useReactivationProps['reactivationPath'] =
      alternateReactivationPath || activateFreeAccessRoute;
  }

  useEffect(() => {
    if (!account?.account) return;
    if (
      import.meta.env.VITE_TARGET_ENV &&
      !['production', 'staging'].includes(import.meta.env.VITE_TARGET_ENV)
    )
      return;

    // handleOnboardingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.account]);

  useEffect(() => {
    handleFreshnessHubUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  function handleFreshnessHubUsers() {
    if (!account) return;
    if (account?.account?.staff_info?.staff) return;
    if (
      account?.account.user_info?.design_name === 'freshness' ||
      account?.account.user_info?.design_version === 'v2'
    ) {
      window.location.href = 'https://www.bumper.com/app/';
    }
  }

  const normalizedRenewalDate =
    account?.account?.subscription_info?.normalize_date;
  const renewalDate =
    typeof normalizedRenewalDate === 'string' && normalizedRenewalDate.trim()
      ? new Date(normalizedRenewalDate)
      : null;
  const renewalDateTime = renewalDate?.getTime();
  const accountInPassedDueState = useCallback(() => {
    if (renewalDateTime === undefined || Number.isNaN(renewalDateTime)) {
      return false;
    }

    return renewalDateTime < Date.now();
  }, [renewalDateTime]);

  // Check if user should be redirected to subscribe page
  useEffect(() => {
    if (!isAuthenticated || !account?.account) return;

    // If user has a paid plan, redirect them from subscribe or freemium activation pages
    // Else, if user has a free plan, redirect them from freemium activation page
    if (!isFreePlan && !accountInPassedDueState()) {
      if (
        location.pathname === '/subscribe' ||
        location.pathname === '/activate-free-access'
      ) {
        navigate('/dashboard');
      }
      return; // End checks for paid users
    } else if (isFreePlan) {
      if (location.pathname === '/activate-free-access') {
        navigate('/dashboard');
      }
      return; // End checks for canceled users
    }

    const freemiumSubscribePageData = getFreemiumSubscribePageData();
    const comesFromSkipSubscribePageFlow = freemiumSubscribePageData.skip_page;

    const hasSkippedSubscribePage =
      account.account.user_settings.hide_freemium_upgrade_prompt;

    if (comesFromSkipSubscribePageFlow && !hasSkippedSubscribePage) {
      (async () => {
        try {
          await updateSetting({
            setting: 'hide_freemium_upgrade_prompt',
            state: true,
          });
        } catch (error) {
          logError(
            'Error updating hide_freemium_upgrade_prompt',
            error as Error,
          );
        }
      })();
    }
  }, [
    isAuthenticated,
    account,
    isFreePlan,
    accountInPassedDueState,
    location.pathname,
    navigate,
    logError,
  ]);

  useEffect(() => {
    // This effect will run once all other checks are complete
    if (account) {
      setIsLoading(false);
    }
  }, [account]);

  function handleOnboardingUsers() {
    // We should redirect all users who have not completed the DQS onboarding
    if (!account?.account) return;
    const sessionOnboardingCompleted = sessionStorage.getItem('dqs_completed');
    if (
      account.account.user_settings.dqs_completed ||
      (sessionOnboardingCompleted && JSON.parse(sessionOnboardingCompleted))
    )
      return;
    const url = location.pathname + location.search + location.hash;
    // Sending the next url to the onboarding page on the
    // location of the AppConfig because when I was sending the values
    // through navigate they weren't being updated with the current path
    // which was causing the user not getting redirected to the correct report generation
    locationAppConfig.state = { next: url };
    navigate('/onboarding');
  }

  useReactivation(useReactivationProps);

  if (!isUserLoggedIn()) {
    invalidateSession();
    const url = location.pathname + location.search + location.hash;
    queryClient.clear();

    setTimeout(() => {
      window.location.reload();
    }, 500);

    return <Navigate to="/login" state={{ next: url }} replace />;
  }
  if (isNonCompliant && !complianceRoutes.includes(location.pathname)) {
    return (
      <Navigate to={`/noncompliance`} state={{ from: location }} replace />
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const shouldShowMemberPerks =
    isMemberPerksEnabled &&
    !isFreePlan &&
    isEligibleForMemberPerks(account) &&
    location.pathname !== '/member-perks' &&
    location.pathname !== '/subscribe';

  if (shouldShowMemberPerks) {
    return <Navigate to="/member-perks" replace />;
  }

  return children;
}
